<template>
  <div class="form__fieldset">
    <h3 class="text-[20px] mt-0 mb-[24px]">{{ in18['attribute_shipping_method'] }}</h3>
    <!-- active form -->

    <div class="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-[8px]">
      <template v-for="shippingMethod in shippingMethods" class="">
        <div
            :class="[ inProgress && 'disabled', selectedShippingComponent && cart.shipping_method?.code === shippingMethod.code && 'hidden' ]"
            class="radio-btn">
          <input
              :id="shippingMethod.code"
              v-model="selectedShippingMethod"
              :disabled="inProgress"
              :value="shippingMethod"
              class="radio-btn__input"
              name="shipping"
              type="radio"
              @change="selectShippingMethod()"
          >
          <label :class="[selectedShippingComponent && cart.shipping_method?.code === shippingMethod.code ? 'shadow-none hover:border-lime-green' : 'cursor-pointer hover:shadow-base']"
                 :for="shippingMethod.code"
                 class="flex flex-col  rounded-[8px] p-[16px] border border-[#C7C7C7] hover:border-lime-green transition-all "
          >
            <picture>
              <source :srcset="shippingMethod.image.webp" type="image/webp">
              <img :alt="shippingMethod.title" :src="shippingMethod.image.source" class="mx-auto h-[24px] mb-[16px]">
            </picture>
            <div class="text-[16px] font-light text-center">{{ shippingMethod.title }}</div>
          </label>
        </div>
      </template>
      <validation-error :errors="errors['shipping_method']"/>
    </div>

    <div v-if="selectedShippingComponent" :class="[ inProgress && 'disabled', 'col-span-full mt-[8px]' ]"
         class="radio-btn relative">

      <div :class="[ 'shadow-none border-lime-green ']"
           class="flex flex-col  rounded-[8px] p-[16px] border border-[#C7C7C7] hover:border-lime-green transition-all "
      >
        <picture>
          <source :srcset="cart.shipping_method.image.webp" type="image/webp">
          <img :alt="cart.shipping_method.title" :src="cart.shipping_method.image.source"
               class="mx-auto h-[24px] mb-[16px]">
        </picture>
        <div class="text-[16px] font-light text-center">{{ cart.shipping_method.title }}</div>
        <div class="flex flex-col text-center">
          <div class="radio-btn__text mt-1 mb-4">{{ cart.shipping_method.description }}</div>
          <component
              :is="selectedShippingComponent"
              :key="selectedShippingMethod.code"
              :addresses="addresses"
              :errors="errors"
              :in18="in18"
              :shipping="shipping"
              :shipping-method="selectedShippingMethod"
              @update-cart="updateCart"
              @update-error="updateError"
              @country-selected="selectShippingMethod"
              @update-shipping-address="selectShippingAddress"
          />
        </div>
      </div>
      <!-- spinner -->
      <div v-if="inProgress" class="absolute z-10 inset-0 bg-white opacity-80 flex items-center justify-center">
        <svg
            class="w-[32px] h-[32px] animate-spin stroke-lime-green"
            preserveAspectRatio="xMidYMid"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
        >
          <circle
              cx="50"
              cy="50"
              fill="none"
              r="35"
              stroke-dasharray="164.93361431346415 56.97787143782138"
              stroke-width="10"
              transform="matrix(1,0,0,1,0,0)"
              xmlns="http://www.w3.org/2000/svg"
          />
        </svg>
      </div>
    </div>

  </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import axios from 'axios';
import ValidationError from '../../features/ValidationError.vue';
import Courier from '../checkout/shipping/Courier.vue';
import Locker from '../checkout/shipping/Locker.vue';
import Shop from '../checkout/shipping/Shop.vue';

// Define props
const props = defineProps({
  in18:       {
    type: Object,
  },
  shipping:   {
    type: Object,
  },
  errors:     {
    type: Object,
  },
  cart:       {
    type: Object,
  },
  inProgress: {
    type: Boolean,
  },
  addresses:  {
    type: Array
  },
});

// Define emits
const emit = defineEmits([
  'update-cart',
  'update-error',
  'update-shipping-method',
  'update-in-progress',
  'update-payment-method',
  'update-shipping-address',
]);

// Reactive state variables
const shippingMethods = ref([]); // Stores available shipping methods
const selectedShippingMethod = ref(null); // Stores the selected shipping method
const selectedShippingAddress = ref(null); // Stores the selected shipping method

// Mapping of shipping method types to component instances
const shippingComponents = {
  courier: Courier,
  locker:  Locker,
  shop:    Shop,
};

watch(() => selectedShippingAddress.value, (address) => {
  // console.log('Selected Address ID from checkout component:', address);
  emit('update-shipping-address', address);
});

// Fetch shipping methods
function selectShippingAddress(address) {
  // console.log('Selected Address ID from checkout component 2:', address);
  emit('update-shipping-address', address);
}

function fetchShippingMethods() {
  axios
      .get('/api/shipping')
      .then((response) => {
        shippingMethods.value = response.data.shippingMethods;
      })
      .catch((error) => {
        console.error('Error fetching shipping methods:', error);
      })
      .finally(() => {
        selectedShippingMethod.value = props.cart?.shipping_method || null;
      });
}

// Call fetchShippingMethods on component setup
fetchShippingMethods();

// Computed property
const selectedShippingComponent = computed(() => {
  if (selectedShippingMethod.value && selectedShippingMethod.value.type) {
    // Return the component instance based on the selected shipping method type
    return shippingComponents[selectedShippingMethod.value.type];
  }
  return null;
});

// Methods
function updateCart(cart) {
  emit('update-cart', cart);
}

function updateError(key, value) {
  emit('update-error', key, value);
}

function selectShippingMethod() {
  if (props.inProgress) {
    return;
  }

  // Notify the parent component about the progress update
  emit('update-in-progress', true);
  emit('update-shipping-method', null);

  // Clear shipping details
  props.shipping.identifier = null;
  props.shipping.region_id = null;
  props.shipping.area_id = null;
  props.shipping.city = '';
  props.shipping.village = '';
  props.shipping.building_num = '';
  props.shipping.house_num = '';
  props.shipping.address = '';
  props.shipping.zip = '';

  return axios
      .post(`/api/cart/shipping/${selectedShippingMethod.value.id}`, props.shipping)
      .then((response) => {
        // Emit events to update the cart and selected shipping method
        emit('update-cart', response.data.cart);
        emit('update-shipping-method', selectedShippingMethod.value);
        ga_add_shipping_info_event(response.data.cart); // Ensure this function is globally available
      })
      .catch((error) => {
        window.toastr.error(error.response.data.message);
      })
      .finally(() => {
        emit('update-in-progress', false);
      });
}
</script>


